import { Component } from "@theme/component";

/** Show the offer link only after the hero and outside the purchase module. */
class ListicleHero extends Component {
  #controller;
  #observer;
  #frame;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    window.addEventListener("scroll", this.#schedule, {
      passive: true,
      signal,
    });
    window.addEventListener("resize", this.#schedule, { signal });
    document.addEventListener("shopify:section:load", this.#schedule, {
      signal,
    });
    document.addEventListener("shopify:section:unload", this.#schedule, {
      signal,
    });
    this.#observer = new ResizeObserver(this.#schedule);
    this.#observer.observe(document.querySelector("#MainContent") || this);
    this.#schedule();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
  }

  #schedule = () => {
    if (this.#frame !== undefined) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      const hero = this.querySelector("[data-listicle-hero-cta]");
      const sticky = this.querySelector("[data-listicle-sticky]");
      const offer = document.getElementById("eden-listicle-offer");
      if (!sticky) return;
      if (!hero || !offer) {
        sticky.hidden = true;
        return;
      }
      const rect = offer.getBoundingClientRect();
      const offerVisible = rect.top < window.innerHeight && rect.bottom > 0;
      sticky.hidden = hero.getBoundingClientRect().bottom >= 0 || offerVisible;
    });
  };
}

/** Native horizontal scrolling works with touch, keyboard, and without JS. */
class ListicleReviews extends Component {
  #controller;
  #observer;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const track = this.querySelector("[data-review-track]");
    const prev = this.querySelector("[data-review-prev]");
    const next = this.querySelector("[data-review-next]");
    const controls = this.querySelector("[data-review-controls]");
    if (!track || !prev || !next || !controls) return;
    const update = () => {
      const overflow = track.scrollWidth - track.clientWidth;
      controls.hidden = overflow <= 1;
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft >= overflow - 1;
    };
    for (const [button, direction] of [
      [prev, -1],
      [next, 1],
    ]) {
      button.addEventListener(
        "click",
        () => {
          track.scrollBy({
            left: direction * track.clientWidth,
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)")
              .matches
              ? "instant"
              : "smooth",
          });
        },
        { signal },
      );
    }
    track.addEventListener("scroll", update, { passive: true, signal });
    this.#observer = new ResizeObserver(update);
    this.#observer.observe(track);
    update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
  }
}

if (!customElements.get("fts-listicle-hero"))
  customElements.define("fts-listicle-hero", ListicleHero);
if (!customElements.get("fts-listicle-reviews"))
  customElements.define("fts-listicle-reviews", ListicleReviews);

/** Keep silent editorial loops offscreen and reduced-motion visitors on posters. */
class ListicleVideo extends Component {
  #controller;
  #observer;
  #visible = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const video = this.querySelector("video");
    if (!video) return;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => {
      if (this.#visible && !motion.matches) {
        video.muted = true;
        video.play()?.catch(() => this.removeAttribute("data-playing"));
      } else {
        video.pause();
        this.removeAttribute("data-playing");
      }
    };
    motion.addEventListener("change", update, { signal });
    video.addEventListener(
      "playing",
      () => {
        if (motion.matches || !this.#visible) {
          update();
          return;
        }
        this.setAttribute("data-playing", "");
      },
      { signal },
    );
    video.addEventListener(
      "pause",
      () => this.removeAttribute("data-playing"),
      { signal },
    );
    video.addEventListener(
      "error",
      () => this.removeAttribute("data-playing"),
      { signal },
    );
    this.#observer = new IntersectionObserver((entries) => {
      this.#visible = entries.some((entry) => entry.isIntersecting);
      update();
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#visible = false;
    this.querySelector("video")?.pause();
    this.removeAttribute("data-playing");
  }
}

if (!customElements.get("fts-listicle-video"))
  customElements.define("fts-listicle-video", ListicleVideo);
